/**
 * Announcements (1.7.6, D-1.7.6-1…3): a message to many people at once — all tenants with an email, the volunteers,
 * both, one program, or only the people ticked — plus extra addresses that are nobody's record. Every recipient is in
 * BCC (To the Director, CC the co-administrator), 50 a message; nothing leaves without a person's press and the
 * cancel window. The box opens with a draft the assistant wrote from the structured fields only, checked by code
 * before it is shown; "Clean it up" fixes spelling and spacing and shows what it changed before the office accepts.
 * After a send the dispatcher reads the mailbox for non-delivery reports and says which address failed and why.
 *
 * Nothing here is a list or a column: an announcement is a file (`_app/announcements/<id>.json`), the Extra
 * recipients list is one file (`_app/extra-recipients.json`) — never People, never a tenant or volunteer record.
 */
import { z } from 'zod';

export type AnnounceLang = 'en' | 'es';

/** The template code on the Outbox row of every announcement batch. */
export const ANNOUNCE_CODE = 'ANNOUNCEMENT';
/** Recipients in BCC on one message. */
export const BCC_BATCH = 50;
/** Batches after the first leave this many seconds apart, in order (the cancel window is the first batch's). */
export const BATCH_GAP_SECONDS = 20;
/** Where the RSVP button goes: the assistant is told to put this on a line of its own; the office may move or remove it. */
export const LINK_PLACEHOLDER = '[RSVP link]';
/** Settings → System: "Draft announcements with the assistant" (default on). */
export const ANNOUNCE_DRAFT_SETTING = 'ANNOUNCE_DRAFT_ASSISTANT';
/** Clean it up may change the length by this share at most; more than that is refused and nothing changes. */
export const CLEANUP_MAX_LENGTH_CHANGE = 0.1;
export const ANNOUNCE_MAX_BODY = 6000;
export const ANNOUNCE_MAX_SUBJECT = 200;
/** The mailbox is read for non-delivery reports for this many days after an announcement's last batch left. */
export const BOUNCE_WATCH_DAYS = 7;
/**
 * What a draft costs is said from its token count. The price is the one assumption: US dollars per million tokens
 * for a small chat model. The real price of the model deployed is on Azure's pricing page (docs/LIMITS.md).
 */
export const DRAFT_PRICE_USD_PER_MILLION_TOKENS = 0.6;

export const ANNOUNCE_PROGRAMS = ['housekeeping', 'companion', 'reading', 'tech'] as const;
export type AnnounceProgram = (typeof ANNOUNCE_PROGRAMS)[number];
export const AUDIENCE_KINDS = ['tenants', 'volunteers', 'both', 'program', 'people'] as const;
export type AudienceKind = (typeof AUDIENCE_KINDS)[number];

/* ------------------------------------------------------------------------------------------------
 * What the office fills in
 * ---------------------------------------------------------------------------------------------- */

const Id = z.string().trim().min(1).max(80).regex(/^[A-Za-z0-9 _.-]+$/);

export const AudienceSchema = z.object({
  kind: z.enum(AUDIENCE_KINDS),
  /** kind 'program' */
  program: z.enum(ANNOUNCE_PROGRAMS).nullable().optional(),
  /** kind 'people': the ones ticked */
  tenantIds: z.array(Id).max(600).optional(),
  volunteerIds: z.array(Id).max(600).optional(),
  /** "Also send to": typed or pasted, separated by commas, semicolons or new lines. */
  extras: z.string().max(20_000).optional(),
});
export type AudienceInput = z.infer<typeof AudienceSchema>;

/** The structured fields the draft is written from — and the only thing about the event the assistant sees. */
export const DraftFieldsSchema = z.object({
  title: z.string().trim().min(1).max(200),
  /** yyyyMMdd, or '' for an announcement that is not about a day */
  dayKey: z.string().regex(/^(\d{8})?$/).default(''),
  /** minutes after midnight, or null */
  minutes: z.number().int().min(0).max(1439).nullable().default(null),
  place: z.string().trim().max(200).default(''),
  bring: z.string().trim().max(300).default(''),
  accessibility: z.string().trim().max(300).default(''),
  /** D-1028-ONELANG-8: the Spanish wording the office typed beside them (a Spanish draft never carries the English words). */
  bringEs: z.string().trim().max(300).default(''),
  accessibilityEs: z.string().trim().max(300).default(''),
  /** Whether the message carries an RSVP button (the event's open link). */
  rsvp: z.boolean().default(false),
  audience: z.enum(['tenants', 'volunteers', 'both']).default('tenants'),
  raffle: z.boolean().default(false),
  raffleRules: z.string().trim().max(400).default(''),
});
export type DraftFields = z.infer<typeof DraftFieldsSchema>;

export const DraftRequestSchema = z.object({
  /** An event: the fields are read from it (and may be completed by `fields`). */
  eventId: Id.nullable().optional(),
  fields: DraftFieldsSchema.partial().optional(),
});

const Words = z.object({ en: z.string().max(ANNOUNCE_MAX_BODY), es: z.string().max(ANNOUNCE_MAX_BODY) });

export const AnnouncementInputSchema = z.object({
  /** A saved draft being changed or sent; absent for a new one. */
  id: Id.nullable().optional(),
  subject: z.object({ en: z.string().trim().max(ANNOUNCE_MAX_SUBJECT), es: z.string().trim().max(ANNOUNCE_MAX_SUBJECT) }),
  body: Words,
  audience: AudienceSchema,
  /** The event it announces (its open link is the RSVP button), or null. */
  eventId: Id.nullable().optional(),
  /** The fields the draft was written from, kept with the announcement. */
  fields: DraftFieldsSchema.partial().nullable().optional(),
});
export type AnnouncementInput = z.infer<typeof AnnouncementInputSchema>;

export const CleanupRequestSchema = z.object({ text: z.string().min(1).max(ANNOUNCE_MAX_BODY), lang: z.enum(['en', 'es']).default('en') });

export const ExtraInputSchema = z.object({
  /** The row being corrected; absent to add one. */
  id: Id.nullable().optional(),
  address: z.string().trim().max(254),
  name: z.string().trim().max(120).default(''),
  note: z.string().trim().max(300).default(''),
  /** They asked for no more emails: kept on the list, never sent to. */
  stopped: z.boolean().default(false),
});
export type ExtraInput = z.infer<typeof ExtraInputSchema>;

/* ------------------------------------------------------------------------------------------------
 * What the API answers
 * ---------------------------------------------------------------------------------------------- */

export interface ExtraRecipient {
  id: string;
  address: string;
  name: string;
  note: string;
  stopped: boolean;
  addedBy: string;
  addedAt: string;
  /** The last non-delivery report for this address, if any. */
  bounced: { at: string; reason: BounceReason } | null;
}

export interface DraftText {
  subject: string;
  body: string;
  /** Who wrote it: the assistant, or the plain wording (Settings → Email wording → Events). */
  source: 'assistant' | 'template';
  /** Why it is the plain wording, in words (the switch is off, the assistant is away, the check refused the draft). */
  why: string | null;
}

export interface DraftResult {
  en: DraftText;
  es: DraftText;
  /** The fields it was written from (an event's own, completed by what was typed). */
  fields: DraftFields;
  /** Tokens the two drafts used; 0 for the plain wording. */
  tokens: number;
}

export interface CleanupResult {
  /** The cleaned text — the same text when nothing needed fixing or the change was refused. */
  text: string;
  changed: boolean;
  /** Why nothing changed, in words (the assistant is away; it changed too much); null when it is a normal answer. */
  refused: string | null;
  tokens: number;
}

export interface DraftCost {
  /** on = the switch is on and the assistant can be reached. */
  state: 'on' | 'off' | 'not-configured';
  last: { tokens: number; at: string } | null;
  month: { key: string; drafts: number; tokens: number };
  pricePerMillion: number;
}

export interface AudiencePerson {
  kind: 'tenant' | 'volunteer';
  id: string;
  name: string;
  unit: string;
  hasEmail: boolean;
}

export interface AudiencePreview {
  tenants: number;
  volunteers: number;
  extra: number;
  total: number;
  /** Tenants in the audience with no email on file, or who did not ask for email — named, so the office can call. */
  noEmail: { tenantId: string; name: string; unit: string }[];
  /** Tenants who asked for no event emails ("Stop these emails") — left out. */
  tenantsStopped: number;
  volunteersNoEmail: number;
  /** Volunteers who stopped their emails — left out. */
  volunteersStopped: number;
  /** Typed addresses that are not addresses — fix them before the press. */
  badAddresses: string[];
  /** Extra addresses left out because they asked for no more emails. */
  extraStopped: string[];
  /** Extra addresses that were a tenant's or a volunteer's already (sent once). */
  duplicates: number;
  /** Messages it leaves in (50 in BCC each, one language a message). */
  batches: number;
  /** D-1028-ONELANG: people whose own record says Spanish (the rest are English). */
  spanishMarked: number;
  /** Of them, how many get the Spanish version (0 while the message has no Spanish text yet). */
  spanish: number;
  to: string[];
  cc: string[];
  delayMinutes: number;
  /** When the first batch would leave if Send were pressed now. */
  firstBatchAt: string;
}

export type AnnouncementStatus = 'draft' | 'queued' | 'sending' | 'sent' | 'cancelled';
export type RecipientStatus = 'waiting' | 'delivered' | 'failed' | 'cancelled' | 'left-out' | 'not-sent';

export interface RecipientRow {
  /** Stable within the announcement (`t:<TenantID>`, `v:<VolunteerID>`, `x:<extra id>`, `…:copy` for a contact person). */
  key: string;
  kind: 'tenant' | 'volunteer' | 'extra';
  id: string;
  name: string;
  unit: string;
  status: RecipientStatus;
  /** failed only */
  reason: BounceReason | null;
  /** failed only: the address that failed (the office needs it to fix it) */
  address: string | null;
  /** failed only: where the address is corrected */
  fix: string | null;
  /** When "Send again to this one" was pressed last. */
  resentAt: string | null;
}

export interface AnnouncementView {
  id: string;
  status: AnnouncementStatus;
  subject: { en: string; es: string };
  body: { en: string; es: string };
  audience: AudienceInput;
  eventId: string | null;
  eventName: string | null;
  fields: Partial<DraftFields> | null;
  createdBy: string;
  createdAt: string;
  sentBy: string | null;
  /** When Send was pressed. */
  sentAt: string | null;
  firstBatchAt: string | null;
  counts: { tenants: number; volunteers: number; extra: number; total: number };
  batches: { total: number; sent: number; waiting: number; cancelled: number; failed: number };
  delivered: number;
  failed: number;
}

export interface AnnouncementDetail extends AnnouncementView {
  recipients: RecipientRow[];
  /** Tenants who had no email when it was sent — named. */
  noEmail: { tenantId: string; name: string; unit: string }[];
}

export interface AnnouncementsResponse {
  announcements: AnnouncementView[];
  /** admin and co-administrator; the scheduler reads. */
  canWrite: boolean;
}

export interface AnnouncementPreview {
  en: { subject: string; html: string; text: string };
  /** null when there is no Spanish text: everyone gets the English. */
  es: { subject: string; html: string; text: string } | null;
  audience: AudiencePreview;
  /** The words that stop a send (no subject, no message, nobody to send to, a bad address, no To), or null. */
  blocked: string | null;
}

export interface AnnouncementSendResult {
  announcement: AnnouncementDetail;
  message: string;
}

export interface AnnouncementHealth {
  /** The last announcement that was sent, or null when none was. */
  last: { id: string; subject: string; at: string; recipients: number; delivered: number; failed: number; batches: number; batchesSent: number } | null;
  /** When the mailbox was last read for non-delivery reports, and what came of it. */
  sweep: { at: string; read: number; matched: number; error: string | null } | null;
}

/* ------------------------------------------------------------------------------------------------
 * Addresses typed or pasted
 * ---------------------------------------------------------------------------------------------- */

export const ADDRESS_SHAPE = /^[^@\s;,<>()"]+@[^@\s;,<>()"]+\.[^@\s;,<>()"]{2,}$/;

/**
 * "Also send to": addresses separated by commas, semicolons or new lines. `Name <a@b.org>` keeps the name. Each
 * address once (case does not matter); anything that is not an address is given back as typed.
 */
export function parseAddresses(text: string): { ok: { address: string; name: string }[]; bad: string[] } {
  const ok: { address: string; name: string }[] = [];
  const bad: string[] = [];
  const seen = new Set<string>();
  for (const raw of String(text ?? '').split(/[,;\r\n]+/)) {
    const piece = raw.trim();
    if (!piece) continue;
    const angle = /^(.*)<([^<>]+)>\s*$/.exec(piece);
    const address = (angle ? angle[2]! : piece).trim().replace(/^mailto:/i, '');
    const name = angle ? angle[1]!.trim().replace(/^["']+|["']+$/g, '').trim() : '';
    if (!ADDRESS_SHAPE.test(address)) {
      bad.push(piece);
      continue;
    }
    if (seen.has(address.toLowerCase())) continue;
    seen.add(address.toLowerCase());
    ok.push({ address, name });
  }
  return { ok, bad };
}

/** Recipients in batches of `size`, in the order given. */
export function inBatches<T>(items: readonly T[], size: number = BCC_BATCH): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/* ------------------------------------------------------------------------------------------------
 * The draft: what must be in it, and what must not
 * ---------------------------------------------------------------------------------------------- */

const squash = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
const digits = (s: string) => s.replace(/\D/g, '');

const EMAIL_IN_TEXT = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const URL_IN_TEXT = /\b(?:https?:\/\/|www\.)[^\s<>()]+/gi;
const PHONE_IN_TEXT = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g;
/** A street address: a number, up to four words, then a street word (English and Spanish). */
const STREET_IN_TEXT = /\b\d{1,5}(?:\s+[A-Za-zÀ-ÿ0-9.'-]+){0,4}\s+(?:street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|place|pl|court|ct|broadway|parkway|pkwy|calle|avenida|av)\b\.?/gi;

export interface DraftCheck {
  ok: boolean;
  /** Required things the draft does not say (the date, the time, the place, the link placeholder). */
  missing: string[];
  /** An address, a phone number or a link in the draft that is in none of the fields. */
  foreign: string[];
}

/**
 * The check a generated draft passes before anyone sees it (D-1.7.6-1): it says every `required` string as given
 * (case and spacing aside), it has the link placeholder when the message carries a button, and it holds no email
 * address, link, phone number or street address that is not in `allowed` (the event's own fields and the office's
 * own contact line).
 */
export function checkDraft(text: string, a: { required: readonly string[]; allowed: readonly string[]; needsLink: boolean }): DraftCheck {
  const flat = squash(text);
  const missing = a.required.filter((r) => r.trim() && !flat.includes(squash(r)));
  if (a.needsLink && !text.includes(LINK_PLACEHOLDER)) missing.push(LINK_PLACEHOLDER);
  const allowedFlat = squash(a.allowed.join(' \n '));
  const allowedDigits = a.allowed.map(digits).filter((d) => d.length >= 7);
  const foreign: string[] = [];
  const add = (s: string) => {
    if (!foreign.includes(s)) foreign.push(s);
  };
  for (const m of text.match(EMAIL_IN_TEXT) ?? []) if (!allowedFlat.includes(m.toLowerCase())) add(m);
  for (const m of text.match(URL_IN_TEXT) ?? []) if (!allowedFlat.includes(m.toLowerCase().replace(/[.,;:!?]+$/, ''))) add(m);
  for (const m of text.match(PHONE_IN_TEXT) ?? []) {
    const d = digits(m).replace(/^1(?=\d{10}$)/, '');
    if (!allowedDigits.some((x) => x.includes(d))) add(m.trim());
  }
  for (const m of text.match(STREET_IN_TEXT) ?? []) if (!allowedFlat.includes(squash(m).replace(/\.$/, ''))) add(m.trim());
  return { ok: !missing.length && !foreign.length, missing, foreign };
}

/** Whether a cleaned text is within the allowed share of the original's length. */
export function withinLength(before: string, after: string, share: number = CLEANUP_MAX_LENGTH_CHANGE): boolean {
  const a = before.trim().length;
  const b = after.trim().length;
  if (!a) return !b;
  return Math.abs(b - a) / a <= share;
}

/**
 * The box's text as it leaves: a line that is only the link placeholder is taken out (the button is added under
 * the words); a placeholder inside a sentence becomes "the button below".
 */
export function bodyForSending(text: string, lang: AnnounceLang): string {
  const lines = String(text ?? '').replace(/\r\n/g, '\n').split('\n');
  const kept = lines.filter((l) => l.trim() !== LINK_PLACEHOLDER);
  return kept
    .join('\n')
    .split(LINK_PLACEHOLDER)
    .join(lang === 'es' ? 'el botón de abajo' : 'the button below')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/* ------------------------------------------------------------------------------------------------
 * What changed: the diff the office sees before it accepts a clean-up
 * ---------------------------------------------------------------------------------------------- */

export interface DiffPart {
  kind: 'same' | 'del' | 'ins';
  text: string;
}

const tokens = (s: string): string[] => s.match(/\s+|[A-Za-zÀ-ÿ0-9'’]+|[^\sA-Za-zÀ-ÿ0-9'’]/g) ?? [];

/** A word-by-word diff (longest common subsequence over words, spaces and punctuation marks). */
export function wordDiff(before: string, after: string): DiffPart[] {
  const a = tokens(before);
  const b = tokens(after);
  const n = a.length;
  const m = b.length;
  const w = m + 1;
  const lcs = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) lcs[i * w + j] = a[i] === b[j] ? lcs[(i + 1) * w + j + 1]! + 1 : Math.max(lcs[(i + 1) * w + j]!, lcs[i * w + j + 1]!);
  const out: DiffPart[] = [];
  const push = (kind: DiffPart['kind'], text: string) => {
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += text;
    else out.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push('same', a[i]!);
      i++;
      j++;
    } else if (lcs[(i + 1) * w + j]! >= lcs[i * w + j + 1]!) push('del', a[i++]!);
    else push('ins', b[j++]!);
  }
  while (i < n) push('del', a[i++]!);
  while (j < m) push('ins', b[j++]!);
  return out;
}

export interface DiffChange {
  /** What was there ('' when something was only added). */
  from: string;
  /** What is there now ('' when something was only taken out). */
  to: string;
  /** Only spaces or line breaks differ. */
  spacing: boolean;
}

/** The diff as a list of changes — "teh" became "the" — for the words beside the colours. */
export function diffChanges(parts: readonly DiffPart[]): DiffChange[] {
  const out: DiffChange[] = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]!;
    if (p.kind === 'same') continue;
    let from = '';
    let to = '';
    while (i < parts.length && parts[i]!.kind !== 'same') {
      if (parts[i]!.kind === 'del') from += parts[i]!.text;
      else to += parts[i]!.text;
      i++;
    }
    i--;
    out.push({ from, to, spacing: !from.trim() && !to.trim() });
  }
  return out;
}

/* ------------------------------------------------------------------------------------------------
 * Non-delivery reports
 * ---------------------------------------------------------------------------------------------- */

export const BOUNCE_REASONS = ['no-address', 'full', 'spam', 'unknown'] as const;
export type BounceReason = (typeof BOUNCE_REASONS)[number];

/** The reason in plain words, as it is kept on the announcement (the screens say it in the reader's language). */
export const BOUNCE_WORDS: Record<BounceReason, { en: string; es: string }> = {
  'no-address': { en: 'the address doesn’t exist', es: 'la dirección no existe' },
  full: { en: 'their mailbox is full', es: 'su buzón está lleno' },
  spam: { en: 'it was rejected as spam', es: 'fue rechazado como correo no deseado' },
  unknown: { en: 'the mail server did not say why', es: 'el servidor de correo no dijo por qué' },
};

export const bounceLine = (reason: BounceReason, lang: AnnounceLang = 'en') => (lang === 'es' ? `No se pudo entregar: ${BOUNCE_WORDS[reason].es}` : `Could not be delivered — ${BOUNCE_WORDS[reason].en}`);

const BOUNCE_FROM = /postmaster@|mailer-daemon@|microsoftexchange[0-9a-f]*@|^mail delivery (sub)?system/i;
const BOUNCE_SUBJECT = /^\s*(undeliverable|undelivered|delivery status notification|mail delivery (failed|subsystem|failure)|returned mail|delivery (has )?fail|failure notice|no se puede entregar|no se pudo entregar)/i;

/** Whether a message in the mailbox is a non-delivery report (by who it is from, or how its subject starts). */
export function isBounceMail(m: { from: string; fromName?: string; subject: string }): boolean {
  return BOUNCE_FROM.test(m.from) || BOUNCE_FROM.test(m.fromName ?? '') || BOUNCE_SUBJECT.test(m.subject);
}

/** The enhanced status code (5.1.1) and the server's own words decide the reason; no code we know is "unknown". */
export function bounceReasonOf(text: string): { reason: BounceReason; code: string | null } {
  const t = String(text ?? '');
  const codes = [...t.matchAll(/\b([245])\.(\d{1,3})\.(\d{1,3})\b/g)].map((m) => `${m[1]}.${m[2]}.${m[3]}`).filter((c) => c !== '2.0.0');
  const code = codes.find((c) => c.startsWith('5.')) ?? codes[0] ?? null;
  const has = (re: RegExp) => re.test(t);
  if (code && /^[45]\.2\.[23]$/.test(code)) return { reason: 'full', code };
  if (has(/mailbox (is )?full|over ?quota|quota exceeded|exceeded (the |its )?storage|insufficient (system )?storage/i)) return { reason: 'full', code };
  if (code && /^5\.7\.\d+$/.test(code)) return { reason: 'spam', code };
  if (has(/\bspam\b|block ?list|blacklist|blocked using|junk|reputation|not authenticated|dmarc|spf check/i)) return { reason: 'spam', code };
  if (code && /^5\.(1\.(0|1|2|3|6|10)|4\.(1|4|310))$/.test(code)) return { reason: 'no-address', code };
  if (has(/(user|recipient|mailbox|address|account)( \S+)? (unknown|not found|does(n't|n’t| not) exist|unavailable|rejected)|no such (user|recipient|mailbox|domain)|couldn(’|')t be found|wasn(’|')t found|domain (name )?(not found|does not exist)|RecipNotFound|recipient address rejected/i)) return { reason: 'no-address', code };
  return { reason: 'unknown', code };
}

export interface ParsedBounce {
  /** The Outbox message it is about (the `X-ABF-Outbox` header the app set), when the report kept the headers. */
  outboxId: string | null;
  /** Every address named in the report (lower case), in the order they appear. */
  addresses: string[];
  reason: BounceReason;
  code: string | null;
}

/** What a non-delivery report says: which message, which addresses, why. */
export function parseBounce(text: string): ParsedBounce {
  const t = String(text ?? '');
  const id = /X-ABF-(?:Outbox|Communication):\s*(COM-[0-9]{14}-[0-9A-F]{8})/i.exec(t)?.[1] ?? /\b(COM-[0-9]{14}-[0-9A-F]{8})\b/.exec(t)?.[1] ?? null;
  const seen = new Set<string>();
  const addresses: string[] = [];
  // the addresses the report itself names as failed come first
  const named = [...t.matchAll(/(?:Final-Recipient|Original-Recipient):\s*rfc822;\s*([^\s<>;]+@[^\s<>;]+)/gi)].map((m) => m[1]!);
  for (const a of [...named, ...(t.match(EMAIL_IN_TEXT) ?? [])]) {
    const k = a.toLowerCase().replace(/[.,;:]+$/, '');
    if (seen.has(k)) continue;
    seen.add(k);
    addresses.push(k);
  }
  return { outboxId: id, addresses, ...bounceReasonOf(t) };
}
